/**
 * TRH Drapery Calculator - Browser Module
 * =========================================================================
 * Manages the drapery calculator form, price calculation, validation,
 * and add-to-cart flow via Shopify's standard /cart/add.js endpoint.
 *
 * Dependencies:
 *   - window.TRHFabricBrowser (shared fabric/trim selection module)
 *
 * Exposes:
 *   - window.TRHDraperyCalculator.init(config)
 *
 * @module TRHDraperyCalculator
 * @version 1.0.0
 */
(function () {
  'use strict';

  /* TRH fabric min fix 2026-10-26: per-fabric minimum + unit (see TRHFabricMin in trh-fabric-browser.js) */
  function _FM() {
    return window.TRHFabricMin || {
      qty: function (y) { return Math.max(Math.ceil(y), 1); },
      info: function () { return { min: 1, unit: 'yard' }; },
      label: function (n) { return n + ' yards'; },
      abbr: function () { return 'yd'; },
      per: function () { return '/yd'; }
    };
  }

  // ---------------------------------------------------------------------------
  // Constants — must mirror api/lib/drapery-calculator.js EXACTLY
  // ---------------------------------------------------------------------------

  /** @const {Object<string, number>} Fullness multiplier for every pleat/heading style. */
  var FULLNESS_MAP = {
    'Flat Panel': 1,
    'Flat Panel w/ Grommets': 1,
    'Flat Panel w/ Rod Pocket': 1,
    'Flat Panel w/ Back Tab': 1,
    'Ripple Fold': 2,
    'Finger Pleat (1.5x Fullness)': 1.5,
    'Cartridge Pleat (2x Fullness)': 2,
    'Goblet Pleat (2x Fullness)': 2,
    'Tailored Pleat (2.5x Fullness)': 2.5,
    'Finger Pleat (2x Fullness)': 2,
    'Windsor Pleat (2x Fullness)': 2,
    'French 2 Finger Pleat (2x Fullness)': 2,
    'Inverted Pleat (2.5x Fullness)': 2.5,
    'Butterfly Pleat (2x Fullness)': 2,
    'French Pleat (2.5x Fullness)': 2.5,
    'Finger Pleat (2.5x Fullness)': 2.5,
    'Finger Pleat (3x Fullness)': 3,
    'Finger Pleat (3.5x Fullness)': 3.5
  };

  /** @const {string[]} Pleat styles that incur the per-pleat surcharge. */
  var PLEAT_STYLES = [
    'Flat Panel w/ Grommets',
    'Ripple Fold',
    'Finger Pleat (1.5x Fullness)',
    'Finger Pleat (2x Fullness)',
    'Finger Pleat (2.5x Fullness)',
    'Finger Pleat (3x Fullness)',
    'Finger Pleat (3.5x Fullness)',
    'French Pleat (2.5x Fullness)',
    'French 2 Finger Pleat (2x Fullness)',
    'Cartridge Pleat (2x Fullness)',
    'Goblet Pleat (2x Fullness)',
    'Windsor Pleat (2x Fullness)',
    'Butterfly Pleat (2x Fullness)',
    'Inverted Pleat (2.5x Fullness)',
    'Tailored Pleat (2.5x Fullness)'
  ];

  /** @const {number} Base labor cost per square foot. */
  var BASE_LABOR_PER_SQFT = 22;

  /** @const {number} Surcharge per pleat when a pleat style is selected. */
  var PLEAT_COST_PER_PLEAT = 8;

  /** @const {number} Trim labor cost per linear inch. */
  var TRIM_LABOR_PER_INCH = 1.5;

  /** @const {number} Absolute minimum price for any single order. */
  var MINIMUM_PRICE = 110;

  /** @const {number} Inches added to each panel width for seam allowance. */
  var SEAM_ALLOWANCE = 4;

  /** @const {number} Inches added to fabric length (top + bottom hems). */
  var LENGTH_ALLOWANCE = 8;

  // ---------------------------------------------------------------------------
  // Module state
  // ---------------------------------------------------------------------------

  /** @type {Object|null} Currently selected fabric from TRHFabricBrowser. */
  var selectedFabric = null;

  /** @type {Object|null} Currently selected trim from TRHFabricBrowser. */
  var selectedTrim = null;

  /** @type {{fabric: number, trim: number}} Last calculated yardage. */
  var lastCalculatedYards = { fabric: 0, trim: 0 };

  /** @type {number} Last calculated total price. */
  var lastCalculatedPrice = 0;

  /** @type {Object} Last calculated price breakdown. */
  var lastCalculatedBreakdown = {
    base: 0,
    liner: 0,
    pleat: 0,
    trim: 0,
    fabric: 0,
    trimProduct: 0
  };

  /** @type {boolean} Whether a calculation has been performed. */
  var hasCalculated = false;

  /** @type {number|null} Numeric Shopify variant ID for the drapery product ($1 variant). */
  var draperyVariantId = null;

  /** @type {string} The form container ID. */
  var formContainerId = 'drapery-form';

  // ---------------------------------------------------------------------------
  // DOM helpers
  // ---------------------------------------------------------------------------

  /**
   * Retrieve a DOM element by ID, returning null if not found.
   *
   * @param {string} id - Element ID.
   * @returns {HTMLElement|null}
   */
  function $(id) {
    return document.getElementById(id);
  }

  /**
   * Hide an error element by ID.
   *
   * @param {string} id - Error span element ID.
   */
  function hideError(id) {
    var el = $(id);
    if (el) {
      el.style.display = 'none';
    }
  }

  /**
   * Show an error element by ID.
   *
   * @param {string} id - Error span element ID.
   */
  function showError(id) {
    var el = $(id);
    if (el) {
      el.style.display = 'block';
    }
  }

  /**
   * Sanitise a user-entered string for safe storage.
   * Strips newlines/tabs, replaces double quotes with single,
   * trims, and caps at 500 characters.
   *
   * @param {string} str - Raw input string.
   * @returns {string} Sanitised output.
   */
  function sanitize(str) {
    if (!str) return 'None';
    return String(str)
      .replace(/[\r\n\t]+/g, ' ')
      .replace(/"/g, "'")
      .trim()
      .slice(0, 500);
  }

  // ---------------------------------------------------------------------------
  // Trim inch computation
  // ---------------------------------------------------------------------------

  /**
   * Compute trim pricing inches and yardage inches based on placement,
   * draw type, dimensions, and quantity.
   *
   * This logic must produce results identical to api/lib/drapery-calculator.js
   * `computeTrimInches()`.
   *
   * @param {string} trimPlacement - 'none','leading','return','both','bottom','picture'.
   * @param {string} drawOption    - The raw draw option value (starts with 'Split Draw' or 'One-Way Draw').
   * @param {number} width         - Window width in inches.
   * @param {number} length        - Window length in inches.
   * @param {number} quantity      - Number of windows.
   * @returns {{ trimPricingInches: number, trimYardageInches: number }}
   */
  function computeTrimInches(trimPlacement, drawOption, width, length, quantity) {
    var trimPricingInches = 0;
    var trimYardageInches = 0;

    if (!trimPlacement || trimPlacement === 'none') {
      return { trimPricingInches: 0, trimYardageInches: 0 };
    }

    var isSplitDraw = drawOption === 'Split Draw';

    switch (trimPlacement) {
      case 'leading':
      case 'return':
        if (isSplitDraw) {
          trimPricingInches = length * 2;
          trimYardageInches = (length + 2) * 2;
        } else {
          trimPricingInches = length;
          trimYardageInches = length + 2;
        }
        break;

      case 'both':
        if (isSplitDraw) {
          trimPricingInches = length * 4;
          trimYardageInches = (length + 2) * 4;
        } else {
          trimPricingInches = length * 2;
          trimYardageInches = (length + 2) * 2;
        }
        break;

      case 'bottom':
        trimPricingInches = width;
        trimYardageInches = width + 2;
        break;

      case 'picture':
        if (isSplitDraw) {
          trimPricingInches = length * 4 + width;
          trimYardageInches = (length + 2) * 4 + (width + 2 + 20);
        } else {
          trimPricingInches = length * 2 + width;
          trimYardageInches = (length + 2) * 2 + (width + 2);
        }
        break;

      default:
        break;
    }

    trimPricingInches *= quantity;
    trimYardageInches *= quantity;

    return { trimPricingInches: trimPricingInches, trimYardageInches: trimYardageInches };
  }

  // ---------------------------------------------------------------------------
  // Validation
  // ---------------------------------------------------------------------------

  /**
   * Validate all required form fields. Optionally shows inline error messages.
   *
   * @param {boolean} [showErrors=true] - Whether to display error elements.
   * @returns {boolean} True if all fields are valid.
   */
  function validateForm(showErrors) {
    if (showErrors === undefined) showErrors = true;

    var ok = true;

    var width = parseFloat($('draperyWidth').value);
    var length = parseFloat($('draperyLength').value);
    var fabricWidth = parseFloat($('draperyFabricWidth').value);
    var quantity = parseInt($('draperyQuantity').value, 10);
    var roomName = $('roomName').value.trim();
    var ownFabricChecked = $('ownFabric').checked;
    var ownTrimChecked = $('ownTrim').checked;
    var customFabricName = $('customFabricName').value.trim();
    var customTrimName = $('customTrimName').value.trim();
    var trimPlacement = $('draperyTrim').value;
    var pleatOption = $('pleatOption').value;
    var grommetColor = $('grommetColor').value;

    // Hide all errors first
    if (showErrors) {
      hideError('roomNameError');
      hideError('widthError');
      hideError('lengthError');
      hideError('fabricError');
      hideError('customFabricNameError');
      hideError('trimError');
      hideError('customTrimNameError');
      hideError('fabricWidthError');
      hideError('quantityError');
      hideError('grommetColorError');
    }

    if (!roomName) {
      if (showErrors) showError('roomNameError');
      ok = false;
    }

    if (isNaN(width) || width < 1) {
      if (showErrors) showError('widthError');
      ok = false;
    }

    if (isNaN(length) || length < 1) {
      if (showErrors) showError('lengthError');
      ok = false;
    }

    if (!ownFabricChecked && !selectedFabric) {
      if (showErrors) showError('fabricError');
      ok = false;
    }

    if (ownFabricChecked && !customFabricName) {
      if (showErrors) showError('customFabricNameError');
      ok = false;
    }

    if (trimPlacement !== 'none' && !ownTrimChecked && !selectedTrim) {
      if (showErrors) showError('trimError');
      ok = false;
    }

    if (ownTrimChecked && trimPlacement !== 'none' && !customTrimName) {
      if (showErrors) showError('customTrimNameError');
      ok = false;
    }

    if (isNaN(fabricWidth) || fabricWidth < 36) {
      if (showErrors) showError('fabricWidthError');
      ok = false;
    }

    if (isNaN(quantity) || quantity < 1) {
      if (showErrors) showError('quantityError');
      ok = false;
    }

    if (pleatOption === 'Flat Panel w/ Grommets' && !grommetColor) {
      if (showErrors) showError('grommetColorError');
      ok = false;
    }

    return ok;
  }

  /**
   * Update the add-to-cart button enabled/disabled state based on silent
   * validation.
   */
  function updateAddToCartButton() {
    var btn = $('addToCartButton');
    if (btn) {
      btn.disabled = !validateForm(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Price calculation
  // ---------------------------------------------------------------------------

  /**
   * Parse the raw draw option value to normalise 'One-Way Draw, Stack Left/Right'
   * into 'Split Draw' or 'One-Way Draw' for calculation purposes.
   *
   * @param {string} rawDrawOption - Value from the draw option select field.
   * @returns {string} 'Split Draw' or 'One-Way Draw'.
   */
  function normalizeDrawOption(rawDrawOption) {
    if (rawDrawOption === 'Split Draw') return 'Split Draw';
    return 'One-Way Draw';
  }

  /**
   * Calculate the drapery price from current form values. Displays the result
   * in the result div.
   *
   * The formula here must match api/lib/drapery-calculator.js exactly.
   */
  function calculateDrapery() {
    if (!validateForm(true)) {
      showError('calculateError');
      return;
    }
    hideError('calculateError');

    var width = parseFloat($('draperyWidth').value);
    var length = parseFloat($('draperyLength').value);
    var fabricWidth = parseFloat($('draperyFabricWidth').value);
    var quantity = parseInt($('draperyQuantity').value, 10);
    var pleat = $('pleatOption').value;
    var liner = parseFloat($('draperyLiner').value);
    var trimPlacement = $('draperyTrim').value;
    var rawDraw = $('drawOption').value;
    var draw = normalizeDrawOption(rawDraw);
    var ownFabricChecked = $('ownFabric').checked;
    var ownTrimChecked = $('ownTrim').checked;

    // 1. Fullness multiplier
    var fullness = FULLNESS_MAP[pleat] || 1;

    // 2. Number of panels
    var panels = draw === 'Split Draw' ? 2 : 1;

    // 3. Fabric width needed per panel (includes seam allowance)
    var fabricWidthNeededPerPanel = (width * fullness / panels) + SEAM_ALLOWANCE;

    // 4. Adjusted length (includes hem allowances)
    var adjustedLength = length + LENGTH_ALLOWANCE;

    // 5. Number of fabric widths needed
    var widthsNeeded;
    if (panels === 2) {
      var coverageWidthNeeded = width * fullness;
      if (coverageWidthNeeded <= fabricWidth) {
        widthsNeeded = 1;
      } else {
        widthsNeeded = panels * Math.ceil(fabricWidthNeededPerPanel / fabricWidth);
      }
    } else {
      widthsNeeded = Math.ceil(fabricWidthNeededPerPanel / fabricWidth);
    }

    // 6. Fabric yardage per set of windows
    var fabricYardsPerSet = (widthsNeeded * adjustedLength) / 36;

    // 7. Total fabric yardage (all windows)
    var totalFabricYards = fabricYardsPerSet * quantity;

    // 8. Square feet per window
    var sqftPerWindow = (width * length) / 144;

    // 9. Total square feet
    var totalSqft = sqftPerWindow * quantity;

    // 10. Base labor cost
    var basePrice = BASE_LABOR_PER_SQFT * totalSqft;

    // 11. Liner cost
    var linerCost = liner * totalSqft;

    // 12-13. Pleat surcharge
    var pleatsNeeded = Math.round(width / 4);
    var pleatCost = PLEAT_STYLES.indexOf(pleat) !== -1
      ? PLEAT_COST_PER_PLEAT * pleatsNeeded * quantity
      : 0;

    // 14-16. Trim inches and cost
    var trimResult = computeTrimInches(trimPlacement, draw, width, length, quantity);
    var trimPricingInches = trimResult.trimPricingInches;
    var trimYardageInches = trimResult.trimYardageInches;
    var trimYards = trimYardageInches / 36;
    var trimLaborCost = trimPlacement !== 'none' ? trimPricingInches * TRIM_LABOR_PER_INCH : 0;

    // 16b. Round yardage up to whole numbers and enforce minimums (fabric: 3 yd, trim: 5 yd)
    /* TRH fabric min fix 2026-10-26: store fabric uses its own minimum and unit (customer fabric keeps 3 yd) */
    var fabricYardsCeiled = ownFabricChecked
      ? Math.max(Math.ceil(totalFabricYards), 3)
      : _FM().qty(totalFabricYards, selectedFabric);
    /* TRH trim min 2026-10-26: 5 yd only for TRH house trims (and the customer's own trim, as before); other brands none */
    var trimYardsCeiled = trimYards > 0 ? Math.max(Math.ceil(trimYards), ownTrimChecked ? 5 : trimMinYards(selectedTrim)) : 0;

    // 17. Fabric material cost (using ceiled yardage)
    var fabricPrice = ownFabricChecked
      ? 0
      : (selectedFabric ? selectedFabric.price * fabricYardsCeiled : 0);

    // 18. Trim material cost (using ceiled yardage)
    var trimProductPrice = (ownTrimChecked || trimPlacement === 'none')
      ? 0
      : (selectedTrim ? selectedTrim.price * trimYardsCeiled : 0);

    // 19. Sum all components
    var total = basePrice + linerCost + pleatCost + trimLaborCost + fabricPrice + trimProductPrice;

    // 20. Enforce minimum price
    total = Math.max(total, MINIMUM_PRICE);

    // 21. Round up to whole dollar
    total = Math.ceil(total);

    // Store results for add-to-cart (ceiled whole-number yardage)
    lastCalculatedYards = { fabric: fabricYardsCeiled, trim: trimYardsCeiled };
    lastCalculatedPrice = total;
    lastCalculatedBreakdown = {
      base: basePrice,
      liner: linerCost,
      pleat: pleatCost,
      trim: trimLaborCost,
      fabric: fabricPrice,
      trimProduct: trimProductPrice
    };
    hasCalculated = true;

    // Build display
    var laborTotal = Math.ceil(basePrice + linerCost + pleatCost + trimLaborCost);
    var fabricCostDisplay = ownFabricChecked
      ? 'Customer Fabric'
      : (selectedFabric ? '$' + Math.ceil(fabricPrice) : '$0.00');
    var trimCostDisplay = trimPlacement === 'none'
      ? 'N/A'
      : (ownTrimChecked
        ? 'Customer Trim'
        : (selectedTrim ? '$' + Math.ceil(trimProductPrice) : '$0.00'));

    var resultHtml =
      '<p style="font-size:18px;font-weight:bold;margin-bottom:10px;color:#28a745;">Total Bundled Cost: $' + total + '</p>' +
      '<div style="font-size:14px;line-height:1.8;border-top:1px solid #ddd;padding-top:10px;color:#7C8C74;">' +
      '<p><strong>Drapery Labor:</strong> $' + laborTotal + '</p>' +
      '<p><strong>Fabric:</strong> ' + (ownFabricChecked ? fabricYardsCeiled + ' yards' : _FM().label(fabricYardsCeiled, selectedFabric)) + ' - ' + fabricCostDisplay + '</p>' +
      (trimPlacement !== 'none'
        ? '<p><strong>Trim:</strong> ' + trimYardsCeiled + ' yards - ' + trimCostDisplay + '</p>'
        : '') +
      '</div>';

    $('draperyResult').innerHTML = resultHtml;

    updateAddToCartButton();
  }

  // ---------------------------------------------------------------------------
  // Add to Cart via /cart/add.js
  // ---------------------------------------------------------------------------

  /**
   * Build the order properties object for the draft order request.
   *
   * @returns {Object} Properties describing the drapery order.
   */
  function buildOrderProperties() {
    var roomName = sanitize($('roomName').value) || 'Not specified';
    var ownFabricChecked = $('ownFabric').checked;
    var ownTrimChecked = $('ownTrim').checked;
    var trimPlacement = $('draperyTrim').value;

    var fabricDetails = '';
    if (ownFabricChecked) {
      var comFabricColor = $('customFabricColor') ? sanitize($('customFabricColor').value) : '';
      fabricDetails = sanitize($('customFabricName').value) + (comFabricColor ? ' - ' + comFabricColor : '') + ' (Customer Fabric)';
    } else if (selectedFabric) {
      var fabricYards = lastCalculatedYards.fabric;
      var fabricTotal = Math.ceil(fabricYards * selectedFabric.price);
      fabricDetails = selectedFabric.title + ' (' + _FM().label(fabricYards, selectedFabric) + ' @ $' + selectedFabric.price.toFixed(2) + _FM().per(selectedFabric) + ' = $' + fabricTotal + ')';
    } else {
      fabricDetails = 'None';
    }

    var trimDetails = '';
    if (trimPlacement === 'none') {
      trimDetails = 'None';
    } else if (ownTrimChecked) {
      var comTrimColor = $('customTrimColor') ? sanitize($('customTrimColor').value) : '';
      trimDetails = sanitize($('customTrimName').value) + (comTrimColor ? ' - ' + comTrimColor : '') + ' (Customer Trim)';
    } else if (selectedTrim) {
      var trimYardsVal = lastCalculatedYards.trim;
      var trimTotal = Math.ceil(trimYardsVal * selectedTrim.price);
      trimDetails = selectedTrim.title + ' (' + trimYardsVal + ' yards @ $' + selectedTrim.price.toFixed(2) + '/yd = $' + trimTotal + ')';
    } else {
      trimDetails = 'Selection Required';
    }

    var pleatOptionValue = $('pleatOption').value;
    var grommetColorValue = pleatOptionValue === 'Flat Panel w/ Grommets'
      ? $('grommetColor').value
      : 'N/A';

    return {
      'Window Name': roomName,
      'Total Price': '$' + lastCalculatedPrice,
      'Width': $('draperyWidth').value + ' inches',
      'Length': $('draperyLength').value + ' inches',
      'Fabric': fabricDetails,
      'Trim Placement': trimPlacement === 'none' ? 'None' : trimPlacement,
      'Trim': trimDetails,
      'Pleat Option': pleatOptionValue,
      'Grommet Color': grommetColorValue,
      'Draw Option': $('drawOption').value,
      'Liner': $('draperyLiner').options[$('draperyLiner').selectedIndex].text,
      'Fabric Width': $('draperyFabricWidth').value + ' inches',
      'Additional Notes': sanitize($('additionalNotes').value) || 'None'
    };
  }

  /**
   * Build the array of cart line items for /cart/add.js.
   * Creates up to 3 items: drapery labor, fabric, and trim — linked by _group_id.
   *
   * @returns {Array<Object>} Items array for Shopify's /cart/add.js endpoint.
   */
  function buildCartItems() {
    var groupId = 'drp-' + Date.now() + '-' + Math.random().toString(36).substr(2, 5);
    var roomName = sanitize($('roomName').value) || 'Not specified';
    var qty = parseInt($('draperyQuantity').value, 10);
    var ownFabricChecked = $('ownFabric').checked;
    var ownTrimChecked = $('ownTrim').checked;
    var trimPlacement = $('draperyTrim').value;

    // Labor cost = total minus material costs (fabric/trim charged as separate line items)
    var fabricMaterialCost = lastCalculatedBreakdown.fabric || 0;
    var trimMaterialCost = lastCalculatedBreakdown.trimProduct || 0;
    var laborCost = Math.max(Math.round(lastCalculatedPrice - fabricMaterialCost - trimMaterialCost), 1);

    // Build properties for the main drapery item
    var properties = buildOrderProperties();
    properties['_group_id'] = groupId;
    properties['_drapery_labor'] = 'true';
    properties['_window_count'] = String(qty);

    var items = [];

    // 1. Drapery labor — $1 variant, quantity = labor cost in dollars
    items.push({
      id: draperyVariantId,
      quantity: laborCost,
      properties: properties
    });

    // 2. Fabric (separate product line item)
    if (!ownFabricChecked && selectedFabric && selectedFabric.id) {
      items.push({
        id: parseInt(selectedFabric.id, 10),
        quantity: lastCalculatedYards.fabric,
        properties: {
          '_group_id': groupId,
          '_is_sub_item': 'true',
          'For': 'Custom Drapery - ' + roomName,
          'Yardage': _FM().label(lastCalculatedYards.fabric, selectedFabric)
        }
      });
    }

    // 3. Trim (separate product line item)
    if (!ownTrimChecked && selectedTrim && selectedTrim.id && trimPlacement !== 'none') {
      items.push({
        id: parseInt(selectedTrim.id, 10),
        quantity: lastCalculatedYards.trim,
        properties: {
          '_group_id': groupId,
          '_is_sub_item': 'true',
          'For': 'Custom Drapery - ' + roomName,
          'Trim Placement': trimPlacement
        }
      });
    }

    return items;
  }

  /**
   * Display a cart/status message in the message container.
   *
   * @param {string}  message - Message text or HTML.
   * @param {boolean} isError - Whether this is an error message.
   */
  function showCartMessage(message, isError) {
    var el = $('item_added_message');
    var msgSpan = $('cartMessage');
    var loadingBar = $('loadingBar');
    var viewBtn = $('viewCartButton');
    var icon = $('success-icon');

    if (loadingBar) loadingBar.style.display = 'none';
    if (msgSpan) {
      msgSpan.style.display = 'block';
      msgSpan.innerHTML = message;
    }
    if (viewBtn) viewBtn.style.display = isError ? 'none' : 'block';
    if (icon) icon.style.display = isError ? 'none' : 'block';

    if (el) {
      if (isError) {
        el.classList.add('error');
      } else {
        el.classList.remove('error');
      }
      el.style.display = 'flex';
    }
  }

  /**
   * Show the loading bar animation in the message container.
   */
  function showLoading() {
    var el = $('item_added_message');
    var msgSpan = $('cartMessage');
    var loadingBar = $('loadingBar');
    var bar = loadingBar ? loadingBar.querySelector('.loading-bar') : null;
    var viewBtn = $('viewCartButton');
    var icon = $('success-icon');

    if (bar) {
      bar.style.width = '0';
      bar.classList.remove('animate');
    }
    if (loadingBar) loadingBar.style.display = 'block';
    if (msgSpan) msgSpan.style.display = 'none';
    if (viewBtn) viewBtn.style.display = 'none';
    if (icon) icon.style.display = 'none';

    if (el) {
      el.classList.remove('error');
      el.style.display = 'flex';
    }

    if (bar) {
      requestAnimationFrame(function () {
        bar.classList.add('animate');
      });
    }
  }

  /**
   * Stop the loading bar animation.
   */
  function hideLoading() {
    var loadingBar = $('loadingBar');
    var bar = loadingBar ? loadingBar.querySelector('.loading-bar') : null;

    if (bar) bar.classList.remove('animate');
    if (loadingBar) loadingBar.style.display = 'none';
  }

  /**
   * Handle the add-to-cart action by adding drapery labor, fabric, and trim
   * as separate line items to the Shopify cart via /cart/add.js.
   */
  function handleAddToCart() {
    if (!validateForm(true)) {
      showError('calculateError');
      return;
    }
    hideError('calculateError');

    var qty = parseInt($('draperyQuantity').value, 10);
    if (qty < 1) {
      showCartMessage('Quantity must be at least 1.', true);
      return;
    }

    if (!hasCalculated || lastCalculatedPrice <= 0) {
      showCartMessage('Please calculate the price first.', true);
      return;
    }

    if (!draperyVariantId) {
      showCartMessage('Configuration error: drapery variant not found.', true);
      return;
    }

    // Disable button during request
    var addBtn = $('addToCartButton');
    if (addBtn) {
      addBtn.disabled = true;
      addBtn.textContent = 'Adding to Cart...';
    }

    showLoading();

    var items = buildCartItems();

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (resp) {
        if (!resp.ok) {
          return resp.json().then(function (data) {
            throw new Error(data.description || data.message || 'Failed to add to cart');
          });
        }
        return resp.json();
      })
      .then(function () {
        hideLoading();
        showCartMessage('Successfully added to cart!', false);

        // Re-enable button so the customer can add another window
        if (addBtn) {
          addBtn.disabled = false;
          addBtn.textContent = 'Add to Cart';
        }
      })
      .catch(function (err) {
        hideLoading();
        showCartMessage('<strong>Error adding to cart</strong><br>' + err.message, true);

        if (addBtn) {
          addBtn.disabled = false;
          addBtn.textContent = 'Add to Cart';
        }
      });
  }

  // ---------------------------------------------------------------------------
  // UI state management
  // ---------------------------------------------------------------------------

  /**
   * Toggle visibility of the grommet color field based on the selected pleat
   * option.
   *
   * @param {string} pleatValue - The selected pleat option value.
   */
  function toggleGrommetColor(pleatValue) {
    var grommetLabel = $('grommetColor') ? $('grommetColor').parentElement : null;
    if (grommetLabel) {
      grommetLabel.style.display = pleatValue === 'Flat Panel w/ Grommets' ? 'block' : 'none';
    }
    if (pleatValue !== 'Flat Panel w/ Grommets') {
      var grommetSelect = $('grommetColor');
      if (grommetSelect) grommetSelect.value = '';
    }
  }

  /**
   * Update trim-related field states (enabled/disabled) based on the
   * selected trim placement.
   *
   * @param {string} trimPlacement - The selected trim placement value.
   */
  function updateTrimFieldStates(trimPlacement) {
    var trimField = $('singleTrimField');
    var browseBtn = $('browseTrimsBtn');
    var ownTrimCheckbox = $('ownTrim');
    var isNone = trimPlacement === 'none';
    var isOwnTrim = ownTrimCheckbox ? ownTrimCheckbox.checked : false;

    if (trimField) trimField.disabled = isNone || isOwnTrim;
    if (browseBtn) browseBtn.disabled = isNone || isOwnTrim;
    if (ownTrimCheckbox) ownTrimCheckbox.disabled = isNone;

    if (isNone) {
      selectedTrim = null;
      if (trimField) {
        trimField.value = '';
        trimField.classList.remove('selected');
      }
      var trimSuggestions = $('trimSearchSuggestions');
      if (trimSuggestions) trimSuggestions.style.display = 'none';
    }
  }

  /**
   * Handle the "Use My Own Fabric" checkbox change.
   */
  function handleOwnFabricChange() {
    var ownFabricCheckbox = $('ownFabric');
    var own = ownFabricCheckbox ? ownFabricCheckbox.checked : false;
    var fabricField = $('singleFabricField');
    var browseBtn = $('browseFabricsBtn');
    var customFabricLabel = $('customFabricName') ? $('customFabricName').parentElement : null;
    var swatchPreview = $('fabricSwatchPreview');
    var fabricWidthField = $('draperyFabricWidth');

    var customFabricColorLabel = $('customFabricColor') ? $('customFabricColor').parentElement : null;
    if (customFabricLabel) customFabricLabel.style.display = own ? 'block' : 'none';
    if (customFabricColorLabel) customFabricColorLabel.style.display = own ? 'block' : 'none';
    if (fabricField) {
      fabricField.disabled = own;
      fabricField.value = own
        ? ''
        : (selectedFabric ? selectedFabric.title + ' ($' + selectedFabric.price.toFixed(2) + _FM().per(selectedFabric) + ')' : '');
      if (own) {
        fabricField.classList.remove('selected');
      } else {
        fabricField.classList.toggle('selected', !!selectedFabric);
      }
    }
    if (browseBtn) browseBtn.disabled = own;
    if (swatchPreview) swatchPreview.style.display = own ? 'none' : (selectedFabric ? 'flex' : 'none');

    if (fabricWidthField) {
      if (own) {
        selectedFabric = null;
        fabricWidthField.removeAttribute('readonly');
        fabricWidthField.style.background = '#fff';
      } else {
        fabricWidthField.setAttribute('readonly', 'readonly');
        fabricWidthField.style.background = '#f0f0f0';
        if (!selectedFabric) {
          fabricWidthField.value = 54;
        }
      }
    }

    updateAddToCartButton();
  }

  /**
   * Handle the "Use My Own Trim" checkbox change.
   */
  function handleOwnTrimChange() {
    var ownTrimCheckbox = $('ownTrim');
    var own = ownTrimCheckbox ? ownTrimCheckbox.checked : false;
    var trimField = $('singleTrimField');
    var browseBtn = $('browseTrimsBtn');
    var customTrimLabel = $('customTrimName') ? $('customTrimName').parentElement : null;
    var customTrimColorLabel = $('customTrimColor') ? $('customTrimColor').parentElement : null;
    var swatchPreview = $('trimSwatchPreview');

    if (customTrimLabel) customTrimLabel.style.display = own ? 'block' : 'none';
    if (customTrimColorLabel) customTrimColorLabel.style.display = own ? 'block' : 'none';
    if (trimField) {
      trimField.disabled = own;
      trimField.value = own
        ? ''
        : (selectedTrim ? selectedTrim.title + ' ($' + selectedTrim.price.toFixed(2) + '/yd)' : '');
      if (own) {
        trimField.classList.remove('selected');
      } else {
        trimField.classList.toggle('selected', !!selectedTrim);
      }
    }
    if (browseBtn) browseBtn.disabled = own;
    if (swatchPreview) swatchPreview.style.display = own ? 'none' : (selectedTrim ? 'flex' : 'none');

    if (own) {
      selectedTrim = null;
    }

    updateAddToCartButton();
  }

  // ---------------------------------------------------------------------------
  // TRHFabricBrowser integration (custom events)
  // ---------------------------------------------------------------------------

  /**
   * Handle the `trh:fabric-selected` custom event from TRHFabricBrowser.
   *
   * @param {CustomEvent} event - Event with fabric data in `detail`.
   */
  function onFabricSelected(event) {
    var fabricData = event.detail && event.detail.fabric ? event.detail.fabric : event.detail;
    if (!fabricData) return;

    selectedFabric = {
      id: fabricData.variantId || fabricData.id || null,
      title: fabricData.title || '',
      price: fabricData.price || 0,
      imageUrl: fabricData.imageUrl || null,
      altText: fabricData.altText || fabricData.title || '',
      fabricWidth: fabricData.fabricWidth || 54,
      minQty: fabricData.minQty || null,
      unit: fabricData.unit || null,
      tags: fabricData.tags || null,
      vendor: fabricData.vendor || ''
    };

    // Update fabric width field
    var fabricWidthField = $('draperyFabricWidth');
    if (fabricWidthField) {
      fabricWidthField.value = selectedFabric.fabricWidth;
    }

    // Recalculate if already calculated
    if (hasCalculated) {
      calculateDrapery();
    }

    hideError('fabricError');
    updateAddToCartButton();
  }

  /**
   * Handle the `trh:trim-selected` custom event from TRHFabricBrowser.
   *
   * @param {CustomEvent} event - Event with trim data in `detail`.
   */
  /* TRH trim min 2026-10-26 (Rachel): only The ReBorn House (TRH) trims have the 5 yd minimum; other
     brands' trims have none (sold by the yard, 1+; a min-N tag on a trim still applies) */
  function trimMinYards(t) {
    if (!t) return 1;
    var n = 0;
    (t.tags || []).forEach(function (x) { x = String(x).toLowerCase(); if (x.slice(0, 4) === 'min-') { var v = parseInt(x.slice(4), 10); if (v > n) n = v; } });
    if (t.vendor === 'The ReBorn House' || /^TRH\d/i.test(String(t.title || ''))) return Math.max(5, n);
    return n > 1 ? n : 1;
  }
  function onTrimSelected(event) {
    var trimData = event.detail && event.detail.trim ? event.detail.trim : event.detail;
    if (!trimData) return;

    selectedTrim = {
      id: trimData.variantId || trimData.id || null,
      title: trimData.title || '',
      price: trimData.price || 0,
      imageUrl: trimData.imageUrl || null,
      altText: trimData.altText || trimData.title || '',
      vendor: trimData.vendor || '',
      tags: trimData.tags || []
    };

    // Recalculate if already calculated
    if (hasCalculated) {
      calculateDrapery();
    }

    hideError('trimError');
    updateAddToCartButton();
  }

  /**
   * Handle the `trh:fabric-cleared` custom event from TRHFabricBrowser.
   */
  function onFabricCleared() {
    selectedFabric = null;

    // Reset fabric width to default
    var fabricWidthField = $('draperyFabricWidth');
    if (fabricWidthField) {
      fabricWidthField.value = 54;
    }

    updateAddToCartButton();
  }

  /**
   * Handle the `trh:trim-cleared` custom event from TRHFabricBrowser.
   */
  function onTrimCleared() {
    selectedTrim = null;
    updateAddToCartButton();
  }

  // ---------------------------------------------------------------------------
  // Event binding
  // ---------------------------------------------------------------------------

  /**
   * Bind all DOM event listeners for the calculator form.
   */
  function bindEvents() {
    // Calculate button
    var calcBtn = $('calculateButton');
    if (calcBtn) {
      calcBtn.addEventListener('click', calculateDrapery);
    }

    // Reset button
    var resetBtn = $('resetButton');
    if (resetBtn) {
      resetBtn.addEventListener('click', function () {
        location.reload();
      });
    }

    // Add to cart button
    var addBtn = $('addToCartButton');
    if (addBtn) {
      addBtn.addEventListener('click', handleAddToCart);
    }

    // Pleat option change — toggle grommet color
    var pleatSelect = $('pleatOption');
    if (pleatSelect) {
      pleatSelect.addEventListener('change', function () {
        toggleGrommetColor(this.value);
        updateAddToCartButton();
      });
    }

    // Trim placement change
    var trimSelect = $('draperyTrim');
    if (trimSelect) {
      trimSelect.addEventListener('change', function () {
        updateTrimFieldStates(this.value);
        updateAddToCartButton();
      });
    }

    // Own fabric checkbox
    var ownFabricCheckbox = $('ownFabric');
    if (ownFabricCheckbox) {
      ownFabricCheckbox.addEventListener('change', handleOwnFabricChange);
    }

    // Own trim checkbox
    var ownTrimCheckbox = $('ownTrim');
    if (ownTrimCheckbox) {
      ownTrimCheckbox.addEventListener('change', handleOwnTrimChange);
    }

    // Form field changes — update add-to-cart button state
    var formFields = [
      'roomName', 'draperyWidth', 'draperyLength', 'draperyFabricWidth',
      'draperyQuantity', 'grommetColor', 'drawOption', 'draperyLiner',
      'customFabricName', 'customTrimName'
    ];
    formFields.forEach(function (fieldId) {
      var field = $(fieldId);
      if (field) {
        field.addEventListener('input', updateAddToCartButton);
        field.addEventListener('change', updateAddToCartButton);
      }
    });

    // Listen for TRHFabricBrowser custom events
    document.addEventListener('trh:fabric-selected', onFabricSelected);
    document.addEventListener('trh:trim-selected', onTrimSelected);
    document.addEventListener('trh:fabric-cleared', onFabricCleared);
    document.addEventListener('trh:trim-cleared', onTrimCleared);
  }

  // ---------------------------------------------------------------------------
  // Initialisation
  // ---------------------------------------------------------------------------

  /**
   * Set initial UI state (grommet visibility, trim field states, button states).
   */
  function setInitialState() {
    // Grommet color hidden by default
    toggleGrommetColor($('pleatOption') ? $('pleatOption').value : '');

    // Trim fields disabled when trim is "none"
    var trimValue = $('draperyTrim') ? $('draperyTrim').value : 'none';
    updateTrimFieldStates(trimValue);

    // Add-to-cart button state
    updateAddToCartButton();
  }

  /**
   * Initialise the Drapery Calculator module.
   *
   * @param {Object}  [config]                      - Configuration options.
   * @param {number}  [config.draperyVariantId]      - Numeric Shopify variant ID for the $1 drapery variant.
   * @param {string}  [config.formId]                - ID of the form container element.
   */
  function init(config) {
    config = config || {};

    if (config.draperyVariantId) {
      draperyVariantId = config.draperyVariantId;
    }
    if (config.formId) {
      formContainerId = config.formId;
    }

    // Wait for DOM if not ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        bindEvents();
        setInitialState();
      });
    } else {
      bindEvents();
      setInitialState();
    }
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  /**
   * @namespace TRHDraperyCalculator
   * @description Browser-side drapery calculator module for The ReBorn House.
   */
  window.TRHDraperyCalculator = {
    /** Initialise the module. */
    init: init,

    /** Programmatically trigger a price calculation. */
    calculate: calculateDrapery,

    /** Validate the form (returns boolean). */
    validate: validateForm,

    /**
     * Get the current state (selected fabric/trim, last price, yardage).
     *
     * @returns {Object} Current calculator state.
     */
    getState: function () {
      return {
        selectedFabric: selectedFabric,
        selectedTrim: selectedTrim,
        lastCalculatedPrice: lastCalculatedPrice,
        lastCalculatedBreakdown: lastCalculatedBreakdown,
        lastCalculatedYards: lastCalculatedYards,
        hasCalculated: hasCalculated
      };
    },

    /**
     * Set the selected fabric programmatically (used by TRHFabricBrowser).
     *
     * @param {Object} fabric - Fabric data object.
     */
    setFabric: function (fabric) {
      selectedFabric = fabric;
      if (fabric && fabric.fabricWidth) {
        var fw = $('draperyFabricWidth');
        if (fw) fw.value = fabric.fabricWidth;
      }
      updateAddToCartButton();
    },

    /**
     * Set the selected trim programmatically (used by TRHFabricBrowser).
     *
     * @param {Object} trim - Trim data object.
     */
    setTrim: function (trim) {
      selectedTrim = trim;
      updateAddToCartButton();
    }
  };
})();
